import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { ClubNavItem, SeasonNavItem, Search } from './components/nav-items';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { ShowPredicted } from '../nav-items/ShowPredicted';
import { LeaderboardTableFetcher } from './components/table/LeaderboardTableFetcher';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { leaderboardSelector } from 'common/store/modules/player-awards/leaderboard/selectors/simple-selectors';
import { setLeaderboardStateItem } from 'common/store/modules/player-awards/leaderboard';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';
import { useGetUrlParams } from 'common/react/components/competition-nav/hooks/use-get-url-params';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';
import { useResetToDefaults } from 'common/react/components/competition-nav/hooks/use-reset-to-defaults';

const NavWrapper = ({ children }) => (
    <div className="competition-nav filter-dropdown">{children}</div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const Leaderboard = ({
    seasonsToShow,
    seasonPid,
    seasonId,
    competitionId,
    shouldHidePredicted,
    title
}) => {
    const nav = useSelector(navSelector);
    const leaderboardState = useSelector(leaderboardSelector);
    const dispatch = useDispatch();
    const leaderboardNavRef = useRef();

    const options = useCompetitionNavigationOptions(nav);
    const windowSize = useWindowSizeV2();

    options.defaultSeason = seasonPid ?? seasonPid;
    options.seasonsToShow = seasonsToShow ?? seasonsToShow;

    const params = useGetUrlParams();

    useEffect(() => {
        dispatch(
            initialiseNav({
                initialValues: {
                    competitionId,
                    seasonId: params.season || seasonId,
                    roundId: params.round || ''
                }
            })
        );
    }, [dispatch]);

    useSyncUrlParams({
        selector: navSelector,
        mapToParams: (items) => ({
            Season: items.season.id,
            Round: items.round.id
        })
    });

    const classPrefix = 'brownlow-leaderboard';

    const shouldHidePredictedBool =
        shouldHidePredicted === 'true' ? true : false;

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    const selectedSeasonPid = useMemo(() => {
        return options?.seasons?.find((season) => season.id === nav.season?.id)
            ?.providerId;
    }, [nav, options]);

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: { ...nav, ...leaderboardState },
        options,
        filtersFields: ['season', 'team', 'showPredicted', 'sortByFavourites'],
        filtersRef: leaderboardNavRef
    });

    const { hasChangedFromDefaults } = useHasChangedFromDefaults({
        state: { nav: nav, team: leaderboardState?.team },
        initialValues: {
            seasonId,
            team: -1
        }
    });

    const resetToDefaults = useResetToDefaults({
        initialValues: {
            competitionId,
            seasonId
        },
        onAfterReset: () => {
            dispatch(
                setLeaderboardStateItem({
                    item: 'team',
                    value: {
                        id: -1
                    }
                })
            );
        }
    });

    return (
        <div className={classPrefix} ref={leaderboardNavRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <div className="player-awards-wrapper">
                            <SeasonNavItem
                                classes="season-nav-item player-awards-season-nav-item"
                                seasonsToShow={seasonsToShow}
                            />
                            <ClubNavItem title={title} />
                        </div>
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <StatsSection.InfoButton
                                    shouldHidePredicted={
                                        shouldHidePredictedBool
                                    }
                                    seasonPid={selectedSeasonPid}
                                    tooltipContent="PlayerAwardsLeaderboardTooltip"
                                    className="p-none"
                                />
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                        {hasChangedFromDefaults && !showResponsiveItems ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults && showResponsiveItems ? (
                        <StatsSection.NavFilterItems>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}

                    <StatsSection.SettingsButton>
                        {!showResponsiveItems && !shouldHidePredictedBool ? (
                            <div
                                className={`${classPrefix}-nav__extras`}
                                style={{
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <ShowPredicted />
                            </div>
                        ) : null}
                        {!showResponsiveItems ? <Search /> : null}
                        <StatsSection.InfoButton
                            shouldHidePredicted={shouldHidePredictedBool}
                            seasonPid={selectedSeasonPid}
                            tooltipContent="PlayerAwardsLeaderboardTooltip"
                            className="p-none"
                        />
                        <SettingsButton />
                    </StatsSection.SettingsButton>

                    {showResponsiveItems ? (
                        <StatsSection.SearchBarMobile>
                            <div className={`${classPrefix}-nav__extras`}>
                                <Search />
                            </div>
                            {!shouldHidePredictedBool ? (
                                <ShowPredicted />
                            ) : null}
                        </StatsSection.SearchBarMobile>
                    ) : null}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentBody>
                        <LeaderboardTableFetcher
                            cssClass={classPrefix}
                            shouldHidePredicted={shouldHidePredictedBool}
                            title={title}
                            sortByFavourites={leaderboardState.sortByFavourites}
                        />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

Leaderboard.propTypes = {
    seasonsToShow: PropTypes.string,
    seasonPid: PropTypes.string,
    seasonId: PropTypes.string,
    competitionId: PropTypes.string,
    shouldHidePredicted: PropTypes.string,
    title: PropTypes.string
};
